const GOOGLE_BOOKS_API_KEY = import.meta.env.VITE_GOOGLE_BOOKS_API_KEY;
const BASE_URL = "https://www.googleapis.com/books/v1/volumes";
import { getBookById } from "../firebase/services/bookServices";
import { getHighestQualityImage } from "../utils/imageUtils";
import { getOrCreateBook } from "../firebase/services/bookServices";
import { stripHtml } from "../utils/textUtils";

export async function searchBooks(
  query,
  startIndex = 0,
  maxResults = 20,
  signal,
  filters = {}
) {
  const retryLimit = 2;
  let attempt = 0;

  // Map our filter names to Google Books categories
  const genreMapping = {
    "Fiction": "fiction",
    "Mystery": "mystery",
    "Science Fiction": "science fiction",
    "Fantasy": "fantasy",
    "Romance": "romance",
    "Thriller": "thriller",
    "Biography": "biography",
    "History": "history",
  };

  while (attempt <= retryLimit) {
    try {
      // Build query with filters
      let searchQuery = query;

      // Add genre filter using subject: operator
      if (filters.genre) {
        const mappedGenre = genreMapping[filters.genre] || filters.genre.toLowerCase();
        searchQuery += `+subject:${encodeURIComponent(mappedGenre)}`;
      }

      // Build URL with sort parameter
      let url = `https://www.googleapis.com/books/v1/volumes?q=${encodeURIComponent(
        searchQuery
      )}&startIndex=${startIndex}&maxResults=${maxResults}`;

      // Add sort parameter
      if (filters.sort === "Newest") {
        url += "&orderBy=newest";
      } else {
        url += "&orderBy=relevance";
      }

      url += `&key=${import.meta.env.VITE_GOOGLE_BOOKS_API_KEY}`;

      const response = await fetch(url, {
        signal,
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const data = await response.json();

      // Check cached data for each book
      const books = await Promise.all(
        (data.items || []).map(async (item) => {
          // First check if we have this book cached
          const cachedBook = await getBookById(item.id);

          if (cachedBook) {
            return {
              id: cachedBook.id,
              googleBooksId: cachedBook.googleBooksId,
              title: cachedBook.title,
              authors: cachedBook.author.split(", "),
              thumbnail: cachedBook.coverUrl,
              publishedDate: cachedBook.publishDate,
              description: stripHtml(cachedBook.description),
            };
          }

          // Fall back to Google Books data if no cache
          return {
            id: item.id,
            googleBooksId: item.id,
            title: item.volumeInfo.title,
            authors: item.volumeInfo.authors || ["Unknown Author"],
            thumbnail: getHighestQualityImage(item.volumeInfo.imageLinks) || "/images/default-book-cover.png",
            publishedDate: item.volumeInfo.publishedDate,
            description: stripHtml(item.volumeInfo.description || ""),
          };
        })
      );

      return books;
    } catch (error) {
      if (error.name === "AbortError") {
        throw error;
      }

      attempt++;

      if (attempt > retryLimit) {
        throw new Error("Failed to fetch books. Please try again.");
      }

      await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
    }
  }
}

// New function to handle storing a book when clicked
export const getAndStoreBook = async (bookId) => {
  const retryLimit = 3;
  let attempt = 0;
  let lastError;

  while (attempt <= retryLimit) {
    try {
      const response = await fetch(
        `${BASE_URL}/${bookId}?key=${GOOGLE_BOOKS_API_KEY}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const googleBook = await response.json();

      if (!response.ok) {
        throw new Error(
          googleBook.error?.message ||
            `Failed to fetch book details: ${response.status}`
        );
      }

      // Store in our database and get the processed book data
      const book = await getOrCreateBook(googleBook);
      return book;
    } catch (error) {
      lastError = error;
      attempt++;

      if (attempt > retryLimit) {
        console.error("Failed all retry attempts:", lastError);
        throw new Error(
          `Failed to fetch and store book after ${retryLimit} attempts: ${lastError.message}`
        );
      }

      // Exponential backoff: wait longer between each retry
      const delay = Math.min(1000 * Math.pow(2, attempt), 10000);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
};

export const searchBooksByTitleAndAuthor = async (title, author) => {
  try {
    const query = `intitle:${encodeURIComponent(
      title
    )}+inauthor:${encodeURIComponent(author)}`;
    const response = await fetch(
      `https://www.googleapis.com/books/v1/volumes?q=${query}&maxResults=5`
    );

    if (!response.ok) {
      throw new Error("Failed to fetch from Google Books API");
    }

    const data = await response.json();
    return data.items || [];
  } catch (error) {
    console.error("Error searching books by title and author:", error);
    return [];
  }
};

export const searchBooksByISBN = async (isbn) => {
  try {
    if (!isbn || isbn.trim() === "") return [];

    const response = await fetch(
      `https://www.googleapis.com/books/v1/volumes?q=isbn:${encodeURIComponent(
        isbn
      )}&maxResults=3`
    );

    if (!response.ok) {
      throw new Error("Failed to fetch from Google Books API");
    }

    const data = await response.json();
    return data.items || [];
  } catch (error) {
    console.error("Error searching books by ISBN:", error);
    return [];
  }
};
